/**
 * One issue: its story, its notes, and the two things that leave this screen —
 * a status change and a message to the contact.
 *
 * «حل شد» REQUIRES A RESULT, and the form makes that visible before the server
 * refuses it. The reports read that text; «حل شد» with no account of how is a
 * row that helps nobody six months later.
 *
 * `contactNotifiedAt` is displayed but never SET here. This service does not
 * send anything and cannot know: the messaging service reports the message
 * SENT, the feedback service hears it and stamps the field. What the button
 * does is queue a message.
 */
import { useMutation, useQuery } from '@apollo/client';
import {
  Badge,
  Button,
  Card,
  Field,
  Spinner,
  TBody,
  TD,
  TR,
  Table,
} from '@meshhub/design-tokens/ui';
import { ArrowRight, MessageSquare, Send } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FormDialog,
  MutationError,
  SelectField,
  TextArea,
} from '../../components/FormKit.web.jsx';
import { formatDateTime } from '../../lib/format.js';
import {
  ISSUE_KIND_LABELS,
  ISSUE_PRIORITY_LABELS,
  ISSUE_STATUS_LABELS,
  NEXT_STATUSES,
  PRIORITY_CLASS,
  STATUS_CLASS,
} from '../../lib/issueLabels.js';
import {
  ADD_ISSUE_COMMENT,
  ASSIGN_ISSUE,
  CHANGE_ISSUE_STATUS,
  ISSUE_QUERY,
  NOTIFY_CONTACT_ABOUT_ISSUE,
  TEMPLATES_QUERY,
} from '../../lib/queries.js';
import { useSession } from '../../lib/session.jsx';
import { useShortcut } from '../../lib/shortcuts.jsx';
import { S } from '../../lib/strings.js';

export function IssueDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useSession();
  const [statusTarget, setStatusTarget] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const [comment, setComment] = useState('');
  const [customerVisible, setCustomerVisible] = useState(false);

  const { data, loading, refetch } = useQuery(ISSUE_QUERY, { variables: { id } });
  const [changeStatus, statusState] = useMutation(CHANGE_ISSUE_STATUS, {
    onCompleted: () => refetch(),
  });
  const [assign, assignState] = useMutation(ASSIGN_ISSUE, { onCompleted: () => refetch() });
  const [addComment, commentState] = useMutation(ADD_ISSUE_COMMENT, {
    onCompleted: () => {
      setComment('');
      refetch();
    },
  });

  useShortcut('Escape', () => navigate('/issues'), {
    description: S.app.close,
    group: S.issues.title,
  });

  const issue = data?.issue;
  if (loading && !issue) return <Spinner className="m-7" />;
  if (!issue) return null;

  return (
    <div className="flex h-screen flex-col">
      <header
        className="flex h-toolbar shrink-0 items-center gap-3 border-b border-border
                   bg-surface px-6"
      >
        <Link to="/issues" className="text-text-muted">
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
        <h1 className="truncate text-lg font-bold">{issue.title}</h1>
        <Badge className={`bg-surface-hover ${STATUS_CLASS[issue.status]}`}>
          {ISSUE_STATUS_LABELS[issue.status]}
        </Badge>
        <Badge className={`bg-surface-hover ${PRIORITY_CLASS[issue.priority]}`}>
          {ISSUE_PRIORITY_LABELS[issue.priority]}
        </Badge>
        {issue.orphaned && (
          <Badge className="bg-surface-hover text-warning">{S.issues.orphaned}</Badge>
        )}

        <div className="ms-auto flex items-center gap-2">
          {(NEXT_STATUSES[issue.status] ?? []).map((next) => (
            <Button key={next} onClick={() => setStatusTarget(next)}>
              {ISSUE_STATUS_LABELS[next]}
            </Button>
          ))}
          {issue.contact && (
            <Button variant="primary" onClick={() => setNotifying(true)}>
              <Send size={13} aria-hidden="true" />
              {S.issues.notify}
            </Button>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-6">
        <MutationError error={statusState.error ?? assignState.error ?? commentState.error} />

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 flex flex-col gap-4">
            <Card className="flex flex-col gap-3">
              <p className="whitespace-pre-wrap text-base">
                {issue.description || S.app.none}
              </p>
              {issue.resolution && (
                <div className="rounded border border-success bg-surface-hover p-3">
                  <p className="text-sm font-medium text-success">{S.issues.resolution}</p>
                  <p className="whitespace-pre-wrap text-sm">{issue.resolution}</p>
                </div>
              )}
            </Card>

            <Card className="flex flex-col gap-3">
              <h2 className="text-md font-bold">{S.issues.comments}</h2>
              {issue.comments.map((entry) => (
                <div key={entry.id} className="border-b border-border pb-2 last:border-b-0">
                  <p className="flex items-center gap-2 text-xs text-text-muted">
                    {entry.authorName ?? entry.authorId}
                    <span>·</span>
                    {formatDateTime(entry.createdAt)}
                    <Badge
                      className={
                        entry.isCustomerVisible
                          ? 'bg-surface-hover text-info'
                          : 'bg-surface-hover text-text-muted'
                      }
                    >
                      {entry.isCustomerVisible
                        ? S.issues.customerVisible
                        : S.issues.internalNote}
                    </Badge>
                  </p>
                  <p className="whitespace-pre-wrap text-sm">{entry.body}</p>
                </div>
              ))}
              <TextArea
                label={S.issues.addComment}
                rows={3}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-text-muted">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={customerVisible}
                    onChange={(event) => setCustomerVisible(event.target.checked)}
                  />
                  {S.issues.customerVisible}
                </label>
                <Button
                  variant="primary"
                  disabled={!comment.trim() || commentState.loading}
                  onClick={() =>
                    addComment({
                      variables: {
                        issueId: issue.id,
                        body: comment.trim(),
                        isCustomerVisible: customerVisible,
                      },
                    })
                  }
                >
                  <MessageSquare size={13} aria-hidden="true" />
                  {S.app.add}
                </Button>
              </div>
            </Card>
          </div>

          <div className="flex flex-col gap-4">
            <Card className="flex flex-col gap-3">
              <Field label={S.contacts.title}>
                {issue.contact ? (
                  <Link to={`/contacts/${issue.contact.id}`} className="text-primary">
                    {issue.contact.displayName}
                  </Link>
                ) : (
                  S.app.none
                )}
              </Field>
              <Field label={S.issues.kind}>{ISSUE_KIND_LABELS[issue.kind]}</Field>
              <Field label={S.issues.type}>{issue.type?.labelFa}</Field>
              <Field label={S.issues.assignee}>
                <span className="flex items-center gap-2">
                  {issue.assigneeName ?? (issue.assigneeId || S.issues.unassigned)}
                  {issue.assigneeId !== me?.id && (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        assign({ variables: { id: issue.id, assigneeId: me?.id } })
                      }
                    >
                      {S.issues.assignToMe}
                    </Button>
                  )}
                </span>
              </Field>
              <Field label={S.issues.reportedAt}>{formatDateTime(issue.reportedAt)}</Field>
              {issue.contactNotifiedAt && (
                <Field label={S.issues.notify}>
                  <span className="text-success">
                    {S.issues.notified.replace(
                      '{date}',
                      formatDateTime(issue.contactNotifiedAt),
                    )}
                  </span>
                </Field>
              )}
            </Card>

            <Card className="flex flex-col gap-2">
              <h2 className="text-md font-bold">{S.issues.history}</h2>
              <Table>
                <TBody>
                  {issue.statusHistory.map((entry) => (
                    <TR key={entry.id}>
                      <TD className="text-sm">
                        {entry.fromStatus
                          ? `${ISSUE_STATUS_LABELS[entry.fromStatus]} → `
                          : ''}
                        {ISSUE_STATUS_LABELS[entry.toStatus]}
                        {entry.reason && (
                          <span className="block text-xs text-text-muted">{entry.reason}</span>
                        )}
                      </TD>
                      <TD className="text-xs text-text-muted">
                        {entry.changedByName ?? entry.changedBy}
                        <span className="block">{formatDateTime(entry.changedAt)}</span>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          </div>
        </div>
      </div>

      <StatusDialog
        target={statusTarget}
        currentStatus={issue.status}
        onClose={() => setStatusTarget(null)}
        onSubmit={(variables) =>
          changeStatus({ variables: { id: issue.id, ...variables } }).then(() =>
            setStatusTarget(null),
          )
        }
      />
      <NotifyDialog
        open={notifying}
        onOpenChange={setNotifying}
        issueId={issue.id}
        onSent={() => refetch()}
      />
    </div>
  );
}

function StatusDialog({ target, currentStatus, onClose, onSubmit }) {
  const [resolution, setResolution] = useState('');
  const [reason, setReason] = useState('');
  const needsResolution = target === 'RESOLVED';
  // Reopening from CLOSED is ADMIN-only AND needs a reason — enforced in the
  // feedback service; the form asks for it so the refusal never happens.
  const needsReason = currentStatus === 'CLOSED';

  return (
    <FormDialog
      open={Boolean(target)}
      onOpenChange={(next) => !next && onClose()}
      title={target ? ISSUE_STATUS_LABELS[target] : ''}
      submitLabel={S.app.confirm}
      canSubmit={
        (!needsResolution || resolution.trim().length > 0) &&
        (!needsReason || reason.trim().length > 0)
      }
      onSubmit={() =>
        onSubmit({
          toStatus: target,
          resolution: resolution.trim() || null,
          reason: reason.trim() || null,
        })
      }
    >
      {needsResolution && (
        <TextArea
          label={S.issues.resolution}
          hint={S.issues.resolutionRequired}
          value={resolution}
          onChange={(event) => setResolution(event.target.value)}
        />
      )}
      {needsReason && (
        <TextArea
          label={S.issues.reopenReason}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      )}
    </FormDialog>
  );
}

function NotifyDialog({ open, onOpenChange, issueId, onSent }) {
  const [templateId, setTemplateId] = useState('');
  const [result, setResult] = useState(null);
  const { data } = useQuery(TEMPLATES_QUERY, {
    variables: { includeInactive: false },
    errorPolicy: 'ignore',
  });
  const [notify, { loading, error }] = useMutation(NOTIFY_CONTACT_ABOUT_ISSUE);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.issues.notify}
      submitLabel={S.messaging.send}
      busy={loading}
      error={error}
      canSubmit={Boolean(templateId)}
      onSubmit={async () => {
        const sent = await notify({ variables: { issueId, templateId } });
        setResult(sent.data?.notifyContactAboutIssue);
        onSent();
      }}
    >
      <SelectField
        label={S.messaging.template}
        value={templateId}
        onChange={(event) => setTemplateId(event.target.value)}
      >
        <option value="">{S.app.none}</option>
        {(data?.messageTemplates ?? []).map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </SelectField>
      {result && (
        <p className="text-sm text-success">
          {S.issues.notifySent} — {result.renderedBody}
        </p>
      )}
    </FormDialog>
  );
}
